import React from 'react';
import './AudioControls.css';
import '../ChatApp.css';

const getLiveMicButtonStyle = (micVolume, { baseScale = 1.02, scaleBoost = 0.12, glowBoost = 34 } = {}) => {
  const normalizedMicVolume = Math.max(0, Math.min((Number(micVolume) || 0) / 72, 1));

  return {
    position: 'relative',
    backgroundColor: '#816194',
    border: '3px solid rgba(255, 255, 255, 0.9)',
    transform: `scale(${baseScale + normalizedMicVolume * scaleBoost})`,
    boxShadow: `0 0 ${28 + normalizedMicVolume * glowBoost}px rgba(129, 97, 148, ${0.28 + normalizedMicVolume * 0.28})`,
    transition: 'transform 0.08s ease-out, box-shadow 0.08s ease-out, border-color 0.08s ease-out, background-color 0.08s ease-out',
    willChange: 'transform, box-shadow',
    zIndex: 3,
  };
};

/**
 * AudioControls component for microphone and audio management
 * Handles mute/unmute and push-to-talk functionality
 */
const AudioControls = ({
  audioMode,
  muted,
  isTalking,
  micVolume,
  onToggleMute,
  onPushToTalkStart,
  onPushToTalkEnd,
  conversationStarted,
  conversationMode
}) => {
  // Don't render audio controls in text-only mode
  if (!conversationStarted || conversationMode === "text") {
    return null;
  }

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch (error) {
      console.warn('Unable to capture push-to-talk pointer:', error);
    }
    onPushToTalkStart(event);
  };

  const handlePointerEnd = (event) => {
    try {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      }
    } catch (error) {
      console.warn('Unable to release push-to-talk pointer capture:', error);
    }
    onPushToTalkEnd(event);
  };

  // Render mute/unmute button for mute mode
  if (audioMode === "mute") {
    return (
      <div className="audio-controls">
        <button
          type="button"
          className={`btn btn--tall ${!muted ? "btn--unmuted" : "btn--muted"}`}
          onClick={onToggleMute}
          style={!muted ? getLiveMicButtonStyle(micVolume) : undefined}
        >
          {muted ? "Unmute Mic" : "Mute Mic"}
        </button>
      </div>
    );
  }

  // Render push-to-talk button for pushToTalk mode
  return (
    <div className="audio-controls">
      <button
        type="button"
        className={`btn btn--tall ${isTalking ? "btn--active" : ""}`}
        style={
          isTalking
            ? getLiveMicButtonStyle(micVolume, { baseScale: 1.08, scaleBoost: 0.22, glowBoost: 82 })
            : undefined
        }
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerEnd}
        onPointerLeave={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onContextMenu={(event) => event.preventDefault()}
      >
        {isTalking ? "Talking..." : "Push to Talk"}
      </button>
    </div>
  );
};

export default AudioControls;
